import { Inject } from '@odinjs/odin';
import { ButtonAPI, DeckAPI, ModalService, PanelAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { MilkAndDerivativesViewerAliases as FragmentAliases, FragmentNames } from '../../../../codegen';
import { Endpoint } from '../../../../constants/endpoints';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { MedicationAndDateHeaderParams } from '../../../commons/header/medication-and-date-header-params';
import { MilkAndDerivativesViewerParams } from './milk-and-derivatives-params';
import './milk-and-derivatives-viewer.css';

@Controller({ component: FragmentAliases.MILK_AND_DERIVATIVES_VIEWER_LAYOUT, fragment: FragmentNames.MILK_AND_DERIVATIVES_VIEWER })
class MilkAndDerivatesViewer extends BaseController {

  @Inject(FragmentAliases.MILK_AND_DERIVATIVES_VIEWER_CONTEXTUAL_BAR)
  milkAndDerivatesContextualBar!: TextAPI;

  @Inject(FragmentAliases.MILK_AND_DERIVATIVES_VIEWER_DECK)
  milkAndDerivatesDeckApi!: DeckAPI;

  @Inject(FragmentAliases.MILK_AND_DERIVATIVES_EXECUTE_BUTTON)
  executeButtonApi!: ButtonAPI;

  @Inject(FragmentAliases.MILK_AND_DERIVATIVES_PANEL_BUTTON)
  panelExecuteApi!: PanelAPI;

  @OnMount(FragmentAliases.MILK_AND_DERIVATIVES_VIEWER_LAYOUT)
  onMountPanel(): void {
    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      patientId,
    });
  }

  @OnMount(FragmentAliases.MILK_AND_DERIVATIVES_VIEWER_DECK)
  onMountDeck(): void {
    this.searchForMedicationDetailInformation();
  }

  searchForMedicationDetailInformation(): void {
    this.milkAndDerivatesDeckApi.setDone(true);
    this.milkAndDerivatesDeckApi.setLoading(true);

    this.findMilkAndDerivativesInformation().then((res) => {
      const responseBody = res as Record<string, Value>;

      MilkAndDerivativesViewerParams.adminSchedule = {
        dilutionDescription: String(responseBody.dilutionDescription),
        materialSequence: Number(responseBody.materialSequence),
        medicationHour: String(responseBody.scheduleDate),
        prescriptionNumber: Number(responseBody.prescriptionNumber),
      };

      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: String(responseBody.materialDescription),
        scheduleDate: String(responseBody.scheduleDate),
        type: 'MILK_AND_DERIVATIVES'
      };

      delete responseBody.processCode;
      responseBody.prescriptionPosition = 'LEFT';
      this.milkAndDerivatesDeckApi.setData([responseBody]);
      this.milkAndDerivatesDeckApi.setLoading(false);
    });
  }

  findMilkAndDerivativesInformation(): Promise<Record<string, Value>> {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    return HttpService.get(Endpoint.EMAR_FIND_MILK_AND_DERIVATIVES_INFORMATION, {
      scheduleSequenceNumber,
    })
      .then(res => {
        const responseBody = res.body as Record<string, Value>;
        this.context.set({ ...responseBody });
        this.validatePanelExecuteWithStatus();
        return responseBody;
      });
  }

  @OnClick(FragmentAliases.MILK_AND_DERIVATIVES_EXECUTE_BUTTON)
  onClickExecuteButton(): void {
    ModalService.openDefault({
      contextData: this.context.getBulkData() as Record<string, Value>,
      fragment: FragmentNames.MILK_AND_DERIVATIVES_EXECUTE,
      id: FragmentNames.MILK_AND_DERIVATIVES_EXECUTE,
      title: 'text.emar-administration',
    })
  }

  private validatePanelExecuteWithStatus(): void {
    const { statusItem } = this.context.getBulkData();
    const hasStatus = ['ADMINISTERED', 'A'].includes(statusItem as string);

    this.panelExecuteApi.setVisible(!hasStatus && this.isAllowedToPerformItems());
  }

  private isAllowedToPerformItems(): boolean {
    const param13Value = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS) as string;
    return ['S', 'D'].includes(param13Value);
  }
}

export default MilkAndDerivatesViewer;
